import React, { memo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, styled, IconButton, Backdrop, CircularProgress, Skeleton, Box } from "@mui/material";
import { ReactComponent as ClientActivityEdit } from "assets/catalogSetting/edit.svg";
import { ReactComponent as TrashIcon } from "assets/icons/trash.svg";
import { ReactComponent as Background } from "assets/icons/bg.svg";
import { useSettingContext } from "context/SettingContext";
import useGetAllUsersData from "features/Setting/users/hooks/useGetAllUsers";
import { ToastContainer, toast } from "react-toastify";
import { usePermissions } from "context/PermissionsContext";
import { findSpecificMenu, findSpecificPage, findSpecificSubMenu, findSpecificSubPage, hasAccessSubPage, isAllowed } from "utils/checkUserRigths";
import RemoveUser from "components/ui/RemoveModal/RemoveModal";
import useDeleteUser from "../hooks/useDeleteUser";
import { getIcon } from "utils/iconsConfig";
import { formatDate } from "utils/formatDate";
import InfoModal from "components/ui/InfoModal/InfoModal";
import CustomeInfoModal from "components/ui/InfoModal/CustomeInfoModal";
import { useGlobalContext } from "context/GlobalContext";

// Styled component for the entire table with padding
const StyledTableContainer = styled(TableContainer)(({}) => ({
  backgroundColor: "transparent",
  maxHeight: "69vh",
  position: "relative",
  marginBottom: "0px",
  // paddingBottom: "5px",
  border: "1px solid #FFFFFF !important",

  // Hide scrollbars for Webkit browsers
  "&::-webkit-scrollbar": {
    width: "0px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "transparent",
  },

  // Hide scrollbars for Firefox
  scrollbarWidth: "none", // For Firefox
}));

const StyledTable = styled(Table)(({}) => ({
  "& th, & td": {
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "22px",
    textAlign: "center",
  },
  "& th": {
    backgroundColor: "#444444",
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({}) => ({
  fontFamily: "Poppins",
  padding: "10px",
  paddingLeft: "15px",
  paddingRight: "15px",
  border: "none",
  position: "sticky",
  top: 0,
  zIndex: 1,
  color: "#FFFFFF",
  "&:first-child ": {
    // borderTopLeftRadius: "5px",
    // borderBottomLeftRadius: "5px",
    // border: "1px solid red !important",
  },
  "&:last-child": {
    // borderTopRightRadius: "5px",
    // borderBottomRightRadius: "5px",
    paddingRight: "15px",
    paddingLeft: "15px",
    borderBottom: "1px solid #FFFFFF !important",
    // border: "1px solid blue !important",
  },
}));

const StyledTableBodyCell = styled(TableCell)(({ color }) => ({
  fontFamily: "Karla",
  padding: "8px !important",
  paddingLeft: "15px !important",
  paddingRight: "15px !important",

  border: "none !important",
  // border: "1px solid pink !important",
  color: color || "#FFFF",
  "&:first-child": {
    paddingLeft: "5px !important",
    // border: "1px solid green !important",
  },
  "&:last-child": {
    paddingRight: "20px !important",
    paddingLeft: "20px !important",
    // borderTop: "none !important",
    borderRight: "none !important",
    borderLeft: "none !important",
    borderBottom: "none !important",
    // border: "none",
    // border: "1px solid green !important",
  },
}));

const StyledTableRow = styled(TableRow)(({}) => ({
  "& td": {
    fontWeight: 400,
    paddingTop: "5px",
    border: "1px solid #FFFFFF !important",
    //  color: "#FFFFFF",
    // color: "blue",
  },
  "&:first-of-type td": {
    paddingTop: "5px",
    // border: "none",
    "&:last-child": {
      // border: "none !important",
    },
  },
  "&:last-child td": {
    paddingBottom: "20px",
  },
}));

const UsersScreen = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const {
    setCreateBtn,
    setCreateBtnName,
    setCancelEditUserModal,
    userCreateStatus,
    setUserCreatedStatus,
    setIsDisable,
    search,
    setSearch,
    setSearchBar,
    userCreateMessage,
    setUserCreateMessage,
    userEditMessage,
    setUserEditMessage,
    userEditStatus,
    setUserEditStatus,
  } = useSettingContext();
  const { setSearchPlaceHolder, setTotals, sortOptions, setSortOptions, sortValue, setSortValue } = useGlobalContext();

  const selectedOption = sortOptions.find((option) => option.id == sortValue);
  const selectedSortBY = selectedOption?.sortBy || "createdAt";
  const selectedSortOrder = selectedOption?.order || "desc";

  const { data, error, isLoading, isError, isFetching, refetch } = useGetAllUsersData({ sortBy: selectedSortBY, sortOrder: selectedSortOrder, search: search });
  const {
    mutate: deleteUser,
    isError: deleteIsError,
    isSuccess: deleteIsSuccess,
    error: deleteError,
    isPending: deletePending,
  } = useDeleteUser({ sortBy: selectedSortBY, sortOrder: selectedSortOrder });
  const [removeUserModal, setRemoveUserModal] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [currectUserData, setCurrentUserData] = useState({});
  const permission = usePermissions();

  const hasAccessCreateUsers = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "User"), "create");
  const hasAccessEditUsers = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "User"), "edit");
  const hasAccessDeleteUsers = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "User"), "delete");
  // const hasAccessAddUser = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Settings", permission), "User"), "add");
  // const hasAccessEditUser = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Settings", permission), "User"), "edit");
  // const hasAccessDeleteUser = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Settings", permission), "User"), "archive");

  const handleUserInfo = (data) => {
    setCurrentUserData(data);
    setInfoModalOpen(true);
  };

  const handlEditUserInfo = (userData) => {
    let state = {
      ...location.state,
      isEditUser: true,
    };
    setIsDisable(true);
    navigation(`/Setting/users/editUser/${userData._id}`, { state });
  };

  const handlDeleteUserInfo = (userData) => {
    setRemoveUserModal(true);
    setCurrentUserData(userData);
  };

  const btnInfo = [
    {
      btnName: "Remove",
      bgColor: "#E77B7B",
      click: () => {
        deleteUser({ userId: currectUserData._id });
        setRemoveUserModal(false);
      },
    },
    {
      btnName: "Keep",
      bgColor: "#50C878",
      click: () => setRemoveUserModal(false),
    },
  ];

  useEffect(() => {
    setCreateBtn(hasAccessCreateUsers);
    setCreateBtnName("Create User");
    setCancelEditUserModal(false);
    setIsDisable(false);
    return () => {
      setCreateBtn(false);
      setCreateBtnName("");
      setIsDisable(false);
    };
  }, [hasAccessCreateUsers]);

  useEffect(() => {
    if (userCreateStatus || userEditStatus) {
      let message = "";
      if (userCreateStatus) {
        message = userCreateMessage;
        setSortValue(null);
      }
      if (userEditStatus) {
        message = userEditMessage;
      }
      toast.success(`${message}`, {
        position: "top-right",
        autoClose: 2000,
      });
      refetch();
    }
    return () => {
      setUserCreatedStatus(false);
      setUserCreateMessage("");
      setUserEditStatus(false);
      setUserEditMessage("");
      if (userCreateStatus) setSortValue(null);
    };
  }, [userCreateStatus, userEditStatus, userEditMessage, userCreateMessage]);

  useEffect(() => {
    if (deleteIsSuccess) {
      toast.success(`User Profile Delete Successfully`, {
        position: "top-right",
        autoClose: 2000,
      });
    }

    if (deleteIsError) {
      toast.success(`${deleteError}`, {
        position: "top-right",
        autoClose: 2000,
      });
    }
  }, [deleteIsError, deleteIsSuccess, deleteError]);

  useEffect(() => {
    setSortOptions([
      { id: 1, name: "First Name (A to Z)", sortBy: "username", order: "asc" },
      { id: 2, name: "First Name (Z to A)", sortBy: "username", order: "desc" },
    ]);
    setSearchBar(true);
    setSearchPlaceHolder("Search By User Name");
    return () => {
      setSearchBar(false);
      setSortOptions([]);
      setSortValue("");
      // setSearch("");
      setSearchPlaceHolder("");
    };
  }, []);

  const TableSkeleton = memo(({ columns = 8, rows = 10 }) => (
    <>
      {Array.from({ length: rows })?.map((_, rowIndex) => (
        <StyledTableRow key={`skeleton-${rowIndex}`}>
          {Array.from({ length: columns })?.map((_, cellIndex) => (
            <StyledTableBodyCell key={`skeleton-cell-${cellIndex}`}>
              <Skeleton variant="text" sx={{ fontSize: "1rem" }} animation="wave" width="100%" />
            </StyledTableBodyCell>
          ))}
        </StyledTableRow>
      ))}
    </>
  ));

  if (!isLoading && !deletePending && (!data?.data?.User || data?.data?.User?.length === 0)) {
    return (
      <div style={{ textAlign: "center", padding: "35px" }}>
        <Background />
      </div>
    );
  }

  return (
    <>
      <div className="Grid-padding" style={{ marginTop: "15px", marginLeft: "6px", marginRight: "11.5px" }}>
        {
          <StyledTableContainer>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>Username</StyledTableHeaderCell>
                  <StyledTableHeaderCell>First Name</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Last Name</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Role</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Email</StyledTableHeaderCell>
                  {/* <StyledTableHeaderCell>Create Role</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Select Role</StyledTableHeaderCell> */}
                  {/* <StyledTableHeaderCell>Password</StyledTableHeaderCell> */}
                  {hasAccessEditUsers && <StyledTableHeaderCell>Edit</StyledTableHeaderCell>}
                  {hasAccessDeleteUsers && <StyledTableHeaderCell>Remove</StyledTableHeaderCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.data?.User.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((curData, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableBodyCell color={true}>{curData?.username}</StyledTableBodyCell>
                    <StyledTableBodyCell color={true}>{curData?.firstName.trim()}</StyledTableBodyCell>
                    <StyledTableBodyCell color={true}>{curData?.lastName.trim()}</StyledTableBodyCell>
                    <StyledTableBodyCell color={curData?.role?.roleName}>{curData?.role?.roleName || "N/A"}</StyledTableBodyCell>
                    <StyledTableBodyCell color={true}>{curData?.email?.trim() || "N/A"}</StyledTableBodyCell>
                    {/* <StyledTableBodyCell color={curData?.createProjectRole?.name}>{curData?.createProjectRole?.name || "N/A"}</StyledTableBodyCell>
                    <StyledTableBodyCell color={curData?.selectProjectRole?.name}>{curData?.selectProjectRole?.name || "N/A"}</StyledTableBodyCell> */}
                    {/* <StyledTableBodyCell>{curData.plainPassword.trim()}</StyledTableBodyCell> */}
                    {hasAccessEditUsers && (
                      <StyledTableBodyCell>
                        <IconButton disableFocusRipple disableRipple sx={{ padding: "0px" }} onClick={() => handlEditUserInfo(curData)}>
                          <ClientActivityEdit />
                        </IconButton>
                      </StyledTableBodyCell>
                    )}
                    {hasAccessDeleteUsers && (
                      <StyledTableBodyCell>
                        <IconButton disableFocusRipple disableRipple onClick={() => handlDeleteUserInfo(curData)}>
                          <TrashIcon />
                        </IconButton>
                      </StyledTableBodyCell>
                  )}
                  </StyledTableRow>
                ))}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        }
      </div>
      <RemoveUser open={removeUserModal} setOpen={setRemoveUserModal} btnInfo={btnInfo} heading="Do you want to Remove the User?" />
      <CustomeInfoModal open={infoModalOpen} setOpen={setInfoModalOpen} fetchData={currectUserData} title="Metadata Details" isUserDetails={true} />
      <ToastContainer />
    </>
  );
};

export default UsersScreen;
